/*
 * cart-added-announce.js  —  issue #29 (WCAG 4.1.3 Status Messages)
 *
 * Adding an item to the cart works, but nothing tells a screen-reader user it
 * succeeded: the header count updates silently (not a live region), and the two
 * visual confirmations (the <cart-popover> and the cart drawer) are both
 * `visibility:hidden`-toggled and auto-dismiss, so neither is announced.
 *
 * This announces the add through a dedicated, always-present visually-hidden
 * live region (`[data-cart-added-live]`; .visually-hidden uses clip:, so it
 * stays in the a11y tree — unlike the visibility:hidden popover/drawer).
 *
 * Design notes (both learned the hard way, from code review):
 *  - Event: `theme:product:add`, NOT `theme:product:added`. The past-tense
 *    `:added` fires at the end of EVERY cart re-sync (build/getCart) — quantity
 *    change, removal, refresh — so it would falsely announce "added to your
 *    cart" when the user REMOVED an item. `theme:product:add` fires only on a
 *    genuine add (verified: removal fires `:added` but not `:add`).
 *  - Success guard: `theme:product:add` is ALSO dispatched on one failure path —
 *    when the add is rejected for exceeding available inventory (theme.js sets
 *    `showCannotAddMoreInCart`, the server returns 422, nothing is added, yet the
 *    event still fires alongside `:add-error`). So we inspect `event.detail.response`:
 *    a genuine add's response is the line item (no `status`); an error response
 *    carries an HTTP `status` (422). We announce only when there is no error status,
 *    otherwise a failed add would falsely confirm success — the exact 4.1.3 defect,
 *    inverted (found in code review).
 *  - Message: the localized label ALONE (`cart.labels.items_added`, carried on
 *    the region's data-added-label). We deliberately do NOT read the popover for
 *    a product name to prefix, for three reasons: the French label carries its
 *    own subject noun ("Article(s) ajouté(s) à votre panier") so a prefix would
 *    break its grammar; the popover isn't rendered on mobile (no name to read
 *    there); and it goes stale (the theme's hidePopover is a no-op typo). Note
 *    the other locales (en/de/es/nl/pt/ru) are subject-less fragments like the
 *    English "added to your cart" — a name would read fine there, but the mobile
 *    and stale-popover problems rule it out regardless.
 *
 * Does NOT touch the cart, the add flow, or the drawer/popover auto-dismiss.
 */
(function () {
  'use strict';
  if (window.__cartAddedAnnounce) return;
  window.__cartAddedAnnounce = true;

  var timer = null;

  function announce(event) {
    // theme:product:add also fires when an add is rejected for max inventory
    // (see header). A genuine add's response has no error `status`; a rejection
    // carries an HTTP status (422). Never announce a failed add as success.
    var response = event && event.detail && event.detail.response;
    if (response && response.status) return;

    var region = document.querySelector('[data-cart-added-live]');
    if (!region) return;
    var message = region.getAttribute('data-added-label') || '';
    if (!message) return;

    // Clear then set so a repeat add of the same item still re-announces (an
    // unchanged textContent would not re-trigger the live region). Coalesce the
    // timer so rapid successive adds announce once, not several times over.
    region.textContent = '';
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(function () {
      region.textContent = message;
      timer = null;
    }, 100);
  }

  document.addEventListener('theme:product:add', announce);
})();
